import { AvailableTaxes, fetchAvailableTaxes } from "api/taxes"
import { BaseRate, RateCardLoading } from "models/ratecard"
import { DEFAULT_FIELDS_KEY, calculate } from "./calculations"
import { FeeOption, fetchBaseRates, fetchMediaPlanFees } from "api/plans"
import { useAtomValue, useUpdateAtom } from "jotai/utils"

import { BillingType } from "routes/Base/Admin/MarketSettings/List/BillingTypes/types"
import { ChangedPlanRecord } from "../../types"
import { Commission } from "models/commissions"
import { FieldsKey } from "./types"
import { MediaType } from "routes/Base/Admin/GlobalMediaTypes/types"
import { Paginated } from "api/types"
import { atom } from "jotai"
import { copyBuyingToPlanning } from "./utils"
import { fetchBillingTypes } from "api/billingtypes"
import { fetchMediaPlanAvailableCommissions } from "api/commissions"
import { get } from "lodash"
import { getBaseRateParams } from "../../RateCard/RateCardDialog"
import { getCommissionsParams } from "../../cells/CommisionCell"
import { getFeesParams } from "../../cells/FeeCell"
import { getTaxesParams } from "../Taxes/TaxesDialog"
import { queryCache } from "react-query"
import { useAuthenticated, usePrecision } from "providers/A12n"
import { useLockedRow } from "../../useLockedRow"
import { useParams } from "react-router-dom"
import { useTargetFieldIds } from "../../Overview/Targets/useTargetFieldIds"
import { useAvailableTargets, useViewType } from "../../utils"
import { useStartDateFieldId } from "./useStartDateFieldId"

type AutoCompute = {
  mediaConfig?: MediaType[]
  onComputed?: (li: ChangedPlanRecord) => void
  budgetPlanning?: "GROSS_COST" | "NET_COST" | "BILLABLE"
  onError?: (error: Error) => void
  fieldsKey?: FieldsKey
  timeZone?: string
  transparent?: boolean
}

const autoCompute = {}

const ac = atom<AutoCompute>(autoCompute)

export function useAutoCompute() {
  const { meta } = useAuthenticated()
  const getTargetIds = useTargetFieldIds()
  const getStartDateId = useStartDateFieldId()
  const availableTargets = useAvailableTargets()
  const { timeZone = "Etc/UTC" } = meta ?? {}
  const { id: planId } = useParams<{ id: string }>()
  const value = useAtomValue(ac)
  const update = useUpdateAtom(ac)
  const { viewTypeValue } = useViewType()
  const { update: updateLockedRow } = useLockedRow()
  const precision = usePrecision()

  const initialize = (
    mediaConfig: MediaType[],
    onComputed: (li: ChangedPlanRecord) => void,
    budgetPlanning: "GROSS_COST" | "NET_COST" | "BILLABLE" = "BILLABLE",
    fieldsKey: FieldsKey = DEFAULT_FIELDS_KEY,
    transparent?: boolean,
    onError?: (e: Error) => void,
  ) =>
    update((v) => ({
      ...v,
      mediaConfig,
      budgetPlanning,
      fieldsKey,
      onComputed,
      transparent,
      onError,
      timeZone,
    }))

  const getPopulations = (li: ChangedPlanRecord) => {
    const mediaId = li?.ancestorMediaId
    const { targetA: targetAId, targetB: targetBId } = getTargetIds(mediaId)
    const aIds = get(li, `${value?.fieldsKey}.${targetAId}`)
    const bIds = get(li, `${value?.fieldsKey}.${targetBId}`)
    const targetA = availableTargets?.find((t) => t.id === aIds?.[0])
    const targetB = availableTargets?.find((t) => t.id === bIds?.[0])
    const startId = getStartDateId(mediaId)
    const startDate = get(li, `${value?.fieldsKey}.${startId}`)
    const year = new Date(startDate).getFullYear()
    return {
      targetA: targetA?.populationMap?.[year],
      targetB: targetB?.populationMap?.[year],
    }
  }

  const getMetricsConstant = (li: ChangedPlanRecord, dataKey?: string) => {
    const mId = dataKey?.split(".")?.[1]
    const media = value.mediaConfig?.find((mf) => mf.id === li.ancestorMediaId)
    const c = media?.mediaFields?.find((f) => f?.id === mId)?.semanticCode
    return c
  }

  const getBaseRates = async (
    baseRateParams: URLSearchParams,
    li: ChangedPlanRecord,
  ) => {
    if (!baseRateParams.get("supplierId")) return undefined

    const mediaId = li?.ancestorMediaId
    const { targetB: targetBId } = getTargetIds(mediaId)
    const targetB = get(li, `${value?.fieldsKey}.${targetBId}`)

    !!targetB && baseRateParams.set("targetIds", targetB)

    const cached = queryCache.getQueryData<{
      baseRate: BaseRate
      loadingResources?: RateCardLoading[]
    }>(["media-plan-base-rate", planId, baseRateParams.toString()])
    if (!!cached) return cached
    try {
      const fetched = await fetchBaseRates(
        "media-plan-base-rate",
        planId,
        baseRateParams.toString(),
      )

      queryCache.setQueryData(
        ["media-plan-base-rate", planId, baseRateParams.toString()],
        fetched,
      )

      return fetched
    } catch (error) {
      if (!value.onError) throw error
      value.onError(error as Error)
    }
  }

  const getCommissions = async (commissionsParams: URLSearchParams) => {
    if (
      commissionsParams.get("commissionSemantic") === "MC" &&
      !commissionsParams.get("supplierId")
    )
      return undefined

    const cached = queryCache.getQueryData<Commission[]>([
      "media-plan-commissions",
      planId,
      commissionsParams.toString(),
    ])
    if (!!cached) return cached
    try {
      const fetched = await fetchMediaPlanAvailableCommissions(
        "media-plan-commissions",
        planId,
        commissionsParams.toString(),
      )

      queryCache.setQueryData(
        ["media-plan-commissions", planId, commissionsParams.toString()],
        fetched,
      )

      return fetched
    } catch (error) {
      if (!value.onError) throw error
      value.onError(error as Error)
    }
  }

  const getFees = async (feesParams: URLSearchParams, qk: string) => {
    if (!feesParams.get("mediaTypeSemanticCode")) return undefined

    const cached = queryCache.getQueryData<Paginated<FeeOption>>([
      `media-plan-${qk}`,
      planId,
      feesParams.toString(),
    ])
    if (!!cached) return cached.data
    try {
      const data = await fetchMediaPlanFees(
        `media-plan-${qk}`,
        planId,
        feesParams.toString(),
      )

      queryCache.setQueryData(
        [`media-plan-${qk}`, planId, feesParams.toString()],
        data,
      )

      return data?.data
    } catch (error) {
      if (!value.onError) throw error
      value.onError(error as Error)
    }
  }

  const getBillingTypes = async () => {
    const cached = queryCache.getQueryData<BillingType[]>(["billing-types"])
    if (!!cached) return cached
    try {
      const fetched = await fetchBillingTypes("billing-types")

      queryCache.setQueryData(["billing-types"], fetched)

      return fetched
    } catch (error) {
      if (!value.onError) throw error
      value.onError(error as Error)
    }
  }

  const getTaxes = async (taxesParams: URLSearchParams) => {
    const cached = queryCache.getQueryData<AvailableTaxes>([
      "available-taxes",
      planId,
      taxesParams.toString(),
    ])
    if (!!cached) return cached
    try {
      const fetched = await fetchAvailableTaxes(
        "available-taxes",
        planId,
        taxesParams.toString(),
      )

      queryCache.setQueryData(
        ["available-taxes", planId, taxesParams.toString()],
        fetched,
      )

      return fetched
    } catch (error) {
      if (!value.onError) throw error
      value.onError(error as Error)
    }
  }

  const compute = async (
    li: ChangedPlanRecord,
    sync?: boolean,
    dataKey?: string,
  ) => {
    if (
      !value.mediaConfig ||
      !value.onComputed ||
      !["package", "line_item"].includes(li.type)
    )
      return false
    try {
      // lock row if sync
      updateLockedRow(li.id)
      // get base rate
      const isBooking = value.fieldsKey === "buyingMediaFields"
      const baseRateParams = getBaseRateParams(value.mediaConfig, li, isBooking)
      const { baseRate, loadingResources: loadings } =
        (await getBaseRates(baseRateParams, li)) || {}

      // get commissions
      const media = value.mediaConfig?.find(
        (mf) => mf.id === li.ancestorMediaId,
      )
      // mc
      const mcParams = getCommissionsParams(
        li,
        value?.fieldsKey,
        media,
        "mediaCommissions",
      )
      const mcCommissions = await getCommissions(mcParams)
      // ac
      const acParams = getCommissionsParams(
        li,
        value?.fieldsKey,
        media,
        "agencyCommissions",
      )
      const acCommissions = await getCommissions(acParams)
      // fees
      const feesParams = getFeesParams(li, value?.fieldsKey, media, "fees")
      const fees = await getFees(feesParams, "fees")
      //passthrough costs
      const passthroughParams = getFeesParams(
        li,
        value?.fieldsKey,
        media,
        "passthroughCosts",
      )
      const passthroughCosts = await getFees(
        passthroughParams,
        "passthrough-fees",
      )
      // billing types
      const billingTypes = await getBillingTypes()
      // taxes
      const taxesParams = getTaxesParams(
        li,
        value?.fieldsKey,
        value.mediaConfig,
        viewTypeValue,
      )
      const taxes = await getTaxes(taxesParams)

      const populations = getPopulations(li)

      const allFees = [...(fees || []), ...(passthroughCosts || [])]

      const metricsConst = getMetricsConstant(li, dataKey)

      // calculate with context
      let appliedLi = li
      const context = {
        mediaConfig: value.mediaConfig,
        budgetPlanning: value.budgetPlanning,
        fieldsKey: value?.fieldsKey,
        timeZone: value?.timeZone,
        fees: allFees,
        baseRate,
        loadings,
        mcCommissions,
        acCommissions,
        billingTypes,
        taxes,
        populations,
        metricsConst,
      }

      /*
        In case of BO we need to merge the buying values to the
        planning ones (some are overwritten, others are not) and then
        re-calculate them
      */

      if (value?.fieldsKey === "buyingMediaFields") {
        // merge planning and buying according to transparency
        appliedLi = copyBuyingToPlanning(appliedLi, media, value?.transparent)
        // planning re-calculation
        appliedLi = calculate(
          appliedLi,
          {
            ...context,
            fieldsKey: "planningMediaFields",
          },
          precision,
        )
      }

      // compute
      const computed = calculate(appliedLi, context, precision)
      if (!!computed) {
        if (sync) {
          updateLockedRow(undefined)
          return computed
        }
        value.onComputed(computed)
      }
    } catch (error) {
      if (!value.onError) throw error
      value.onError(error as Error)
    }
  }

  return { initialize, compute }
}
